<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <oxd-table-filter :filter-title="員工打卡匯出" :collapsed="false">
    <oxd-form @submit-valid="onClickView">
      <oxd-form-row>
        <oxd-grid :cols="3" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <employee-autocomplete
              v-model="filters.employee"
              :rules="rules.employee"
              :params="{
                includeEmployees: 'currentAndPast',
              }"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="filters.locationId"
              type="select"
              label="店舖"
              :rules="rules.location"
              :options="locations"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="filters.dateRangeOption"
              type="select"
              label="日期"
              :rules="rules.selected"
              :options="dateRangeOptions"
            />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>

      <oxd-divider />

      <oxd-form-actions>
        <oxd-button
          display-type="secondary"
          label="匯出糧單"
          type="button"
          @click="onExportReport"
        />
        <oxd-button
          display-type="secondary"
          label="匯出打卡PDF"
          type="button"
          @click="onExportTimesheet"
        />
      </oxd-form-actions>
    </oxd-form>
  </oxd-table-filter>
</template>

<script>
import {computed, ref} from 'vue';
import {
  required,
  validSelection,
  validDateFormat,
} from '@/core/util/validation/rules';
import {
  freshDate,
  parseDate,
  parseTime,
  formatTime,
  formatDate,
  getStandardTimezone,
} from '@/core/util/helper/datefns';
import {navigate} from '@/core/util/helper/navigation';
import {yearRange} from '@/core/util/helper/year-range';
import useLocale from '@/core/util/composable/useLocale';
import {APIService} from '@/core/util/services/api.service';
import useDateFormat from '@/core/util/composable/useDateFormat';
import EmployeeAutocomplete from '@/core/components/inputs/EmployeeAutocomplete';

export default {
  components: {
    'employee-autocomplete': EmployeeAutocomplete,
  },

  props: {
    date: {
      type: String,
      default: null,
    },
    employee: {
      type: Object,
      required: true,
      default: null,
    },
    locations: {
      type: Array,
      default: null,
    },
    accesstoken: {
      type: String,
      default: null,
    },
    empnumber: {
      type: String,
      default: null,
    },
  },

  setup(props) {
    console.log('props', props);
    // 用嚟格式化日期成 "yyyy-mm-dd" 格式（獨立於全局 formatDate）
    const simpleFormatDate = (date) => {
      const year = date.getFullYear();
      const month = ('0' + (date.getMonth() + 1)).slice(-2);
      const day = ('0' + date.getDate()).slice(-2);
      return `${year}-${month}-${day}`;
    };

    // 動態生成最近 12 個月選項，每個選項包含該月第一日同最後一日
    const monthOptions = computed(() => {
      const options = [];
      const today = new Date();
      for (let i = 0; i < 12; i++) {
        const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
        const year = d.getFullYear();
        const month = ('0' + (d.getMonth() + 1)).slice(-2);
        const label = `${year}-${month}`;
        const fromDate = simpleFormatDate(new Date(year, d.getMonth(), 1));
        const toDate = simpleFormatDate(new Date(year, d.getMonth() + 1, 0));
        options.push({
          id: `month-${i}`,
          label,
          fromDate,
          toDate,
        });
      }
      return options;
    });

    // 將月選項同週選項合併
    const dateRangeOptions = computed(() => {
      return monthOptions.value;
    });

    const filters = ref({
      employee: null,
      locationId: null,
      dateRangeOption: null,
    });

    const {locale} = useLocale();
    const {jsDateFormat, userDateFormat, timeFormat, jsTimeFormat} =
      useDateFormat();

    const rules = {
      date: [required, validDateFormat(userDateFormat)],
      employee: [validSelection],
      location: [validSelection],
      selected: [
        (value) => {
          if (!value) {
            return '請選擇日期';
          }
          return true;
        },
      ],
    };

    return {
      rules,
      filters,
      dateRangeOptions,
    };
  },

  methods: {
    onExportReport() {
      if (!this.filters.employee?.id && !this.filters.locationId?.id) {
        alert('請選擇員工或店舖');
        return;
      }
      if (!this.filters.dateRangeOption) {
        alert('請選擇日期');
        return;
      }
      const baseUrl = `${window.appGlobal.baseUrl}/../api/ExportReport.php`;
      const queryParameters = new URLSearchParams({
        employeeId: this.filters.employee?.id || '',
        locationId: this.filters.locationId?.id || '',
        dateRangeOption: this.filters.dateRangeOption.label || '',
      }).toString();
      const downUrl = `${baseUrl}?${queryParameters}`;
      window.open(downUrl, '_blank');
    },
    onExportTimesheet() {
      const baseUrl = `${window.appGlobal.baseUrl}/../api/ExportTimesheet.php`;
      const queryParameters = new URLSearchParams({
        employeeId: this.filters.employee?.id || '',
        locationId: this.filters.locationId?.id || '',
        fromDate: this.filters.dateRangeOption.fromDate || '',
        toDate: this.filters.dateRangeOption.toDate || '',
      }).toString();
      const downUrl = `${baseUrl}?${queryParameters}`;
      window.open(downUrl, '_blank');
    },
  },
};
</script>
